import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { examApi, classesApi, subjectsApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, ChevronRight, Pencil, Save, RotateCcw,
} from 'lucide-react';

const QUESTION_TYPES = ['MCQ', 'Short', 'Long', 'Case Study', 'True/False', 'Fill in the Blank'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const BLOOMS = ['Remember', 'Understand', 'Apply', 'Analyse', 'Evaluate', 'Create'];
const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'Other'];
const LANGUAGES = ['English', 'Hindi', 'Kannada', 'Tamil', 'Marathi', 'Bengali'];

const AIGenerateQuestionsDialog = ({ open, onOpenChange, onSaved }) => {
  const [step, setStep] = useState(1);   // 1 = config, 2 = review
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [syllabusList, setSyllabusList] = useState([]);

  const [form, setForm] = useState({
    class_id: '',
    subject_id: '',
    board: 'CBSE',
    language: 'English',
    chapters_selected: [],     // string list from syllabus checkboxes
    chapters_extra: '',         // extra free-text chapters
    count: 8,
    type_mix: { MCQ: 3, Short: 3, Long: 2 },
    difficulty_mix: { Easy: 3, Medium: 3, Hard: 2 },
    bloom_targets: ['Remember', 'Understand', 'Apply'],
    extra_prompt: '',
  });

  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [editIdx, setEditIdx] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [c, s, syl] = await Promise.all([
        classesApi.getAll(), subjectsApi.getAll(), examApi.listSyllabus(),
      ]);
      setClasses(c.data.classes || []);
      setSubjects(s.data.subjects || []);
      setSyllabusList(syl.data.syllabus || []);
    })();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStep(1); setProposals([]); setSelectedIdx(new Set());
      setEditIdx(null); setEditDraft(null);
    }
  }, [open]);

  const filteredSubjects = useMemo(() => {
    if (!form.class_id) return subjects;
    return subjects.filter(s => s.class_id === form.class_id);
  }, [subjects, form.class_id]);

  const syllabusChapters = useMemo(() => {
    const found = syllabusList.find(s =>
      s.class_id === form.class_id && s.subject_id === form.subject_id);
    return found?.chapters || [];
  }, [syllabusList, form.class_id, form.subject_id]);

  const toggleChapter = (ch) => {
    setForm(f => ({
      ...f,
      chapters_selected: f.chapters_selected.includes(ch)
        ? f.chapters_selected.filter(c => c !== ch)
        : [...f.chapters_selected, ch],
    }));
  };

  const setMix = (key, sub, val) => {
    setForm(f => ({ ...f, [key]: { ...f[key], [sub]: parseInt(val) || 0 } }));
  };

  const toggleBloom = (b) => {
    setForm(f => ({
      ...f,
      bloom_targets: f.bloom_targets.includes(b)
        ? f.bloom_targets.filter(x => x !== b)
        : [...f.bloom_targets, b],
    }));
  };

  const buildChapters = () => {
    const extras = form.chapters_extra
      .split('\n').map(s => s.trim()).filter(Boolean);
    return [...form.chapters_selected, ...extras];
  };

  const sumOf = (obj) => Object.values(obj).reduce((a, b) => a + (parseInt(b) || 0), 0);

  const generate = async () => {
    if (!form.class_id || !form.subject_id) { toast.error('Choose class & subject'); return; }
    const chapters = buildChapters();
    if (chapters.length === 0) {
      toast.error('Select at least one chapter (or type one)'); return;
    }
    const typeSum = sumOf(form.type_mix);
    const diffSum = sumOf(form.difficulty_mix);
    if (typeSum !== form.count || diffSum !== form.count) {
      toast.error(`Type mix (${typeSum}) and Difficulty mix (${diffSum}) must each sum to ${form.count}`);
      return;
    }
    setGenerating(true);
    try {
      const { data } = await examApi.aiGenerateQuestions({
        class_id: form.class_id,
        subject_id: form.subject_id,
        chapters,
        count: form.count,
        type_mix: form.type_mix,
        difficulty_mix: form.difficulty_mix,
        bloom_targets: form.bloom_targets,
        board: form.board,
        language: form.language,
        extra_prompt: form.extra_prompt,
      });
      const props = (data.proposals || []).map((p, i) => ({
        ...p,
        _key: `q-${Date.now()}-${i}`,   // stable React key across edits/toggles
      }));
      setProposals(props);
      setSelectedIdx(new Set(props.map((_, i) => i)));  // pre-select all
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = (i) => {
    setEditIdx(i);
    setEditDraft({ ...proposals[i], options: proposals[i].options || [] });
  };

  const saveEdit = () => {
    const next = [...proposals];
    next[editIdx] = editDraft;
    setProposals(next);
    setEditIdx(null); setEditDraft(null);
  };

  const toggleSelect = (i) => {
    setSelectedIdx(s => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };

  const saveSelected = async () => {
    const picks = Array.from(selectedIdx).map(i => proposals[i]).filter(Boolean);
    if (picks.length === 0) { toast.error('Pick at least one question to save'); return; }
    setSaving(true);
    try {
      const payload = {
        questions: picks.map(p => ({
          class_id: form.class_id,
          subject_id: form.subject_id,
          chapter: p.chapter || '',
          topic: p.topic || '',
          question_type: p.question_type,
          question_text: p.question_text,
          marks: Number(p.marks) || 1,
          difficulty: p.difficulty || 'Medium',
          learning_outcome: p.learning_outcome || '',
          competency_tag: p.competency_tag || '',
          options: p.options || null,
          correct_answer: p.correct_answer || '',
          explanation: p.explanation || '',
        })),
      };
      const { data } = await examApi.aiSaveQuestions(payload);
      toast.success(`Saved ${data.saved} question${data.saved === 1 ? '' : 's'} to bank`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Question Generator
            <Badge variant="outline" className="ml-2 text-xs">
              {step === 1 ? 'Step 1: Configure' : `Step 2: Review (${proposals.length})`}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>Class *</Label>
                <Select value={form.class_id}
                        onValueChange={(v) => setForm(f => ({ ...f, class_id: v, subject_id: '' }))}>
                  <SelectTrigger data-testid="ai-class"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject *</Label>
                <Select value={form.subject_id}
                        onValueChange={(v) => setForm(f => ({ ...f, subject_id: v }))}>
                  <SelectTrigger data-testid="ai-subject"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {filteredSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Board</Label>
                <Select value={form.board} onValueChange={(v) => setForm(f => ({ ...f, board: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BOARDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm(f => ({ ...f, language: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Chapters from Syllabus</Label>
              {syllabusChapters.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1">
                  No saved chapters for this class+subject. Add chapters in <b>Syllabus</b> page or type below.
                </p>
              ) : (
                <div className="border rounded p-2 max-h-32 overflow-y-auto grid grid-cols-2 gap-1 mt-1">
                  {syllabusChapters.map(ch => (
                    <label key={ch} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-muted/50 rounded">
                      <Checkbox checked={form.chapters_selected.includes(ch)}
                                onCheckedChange={() => toggleChapter(ch)} />
                      <span className="truncate">{ch}</span>
                    </label>
                  ))}
                </div>
              )}
              <Label className="text-xs mt-2 block">Extra chapters / topics (one per line, optional)</Label>
              <Textarea rows={2} value={form.chapters_extra}
                        placeholder="Photosynthesis&#10;Cell structure"
                        onChange={e => setForm(f => ({ ...f, chapters_extra: e.target.value }))}
                        data-testid="ai-chapters-extra" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Total Questions</Label>
                <Input type="number" min="1" max="30" value={form.count}
                       onChange={e => setForm(f => ({ ...f, count: parseInt(e.target.value) || 1 }))}
                       data-testid="ai-count" />
              </div>
              <div>
                <Label className="text-xs">
                  Type Mix <span className={sumOf(form.type_mix) === form.count ? 'text-green-600' : 'text-red-500'}>
                    (sum {sumOf(form.type_mix)})
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {QUESTION_TYPES.map(t => (
                    <div key={t} className="flex items-center gap-1">
                      <span className="text-xs w-20 truncate">{t}</span>
                      <Input type="number" min="0" className="h-7 text-xs"
                             value={form.type_mix[t] || 0}
                             onChange={e => setMix('type_mix', t, e.target.value)}
                             data-testid={`ai-type-${t}`} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">
                  Difficulty <span className={sumOf(form.difficulty_mix) === form.count ? 'text-green-600' : 'text-red-500'}>
                    (sum {sumOf(form.difficulty_mix)})
                  </span>
                </Label>
                <div className="space-y-1 mt-1">
                  {DIFFICULTIES.map(d => (
                    <div key={d} className="flex items-center gap-1">
                      <span className="text-xs w-20">{d}</span>
                      <Input type="number" min="0" className="h-7 text-xs"
                             value={form.difficulty_mix[d] || 0}
                             onChange={e => setMix('difficulty_mix', d, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Bloom&apos;s Taxonomy targets</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {BLOOMS.map(b => (
                  <button key={b} type="button" onClick={() => toggleBloom(b)}
                          className={`px-3 py-1 rounded-full text-xs border transition ${
                            form.bloom_targets.includes(b)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background hover:bg-muted'}`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Additional guidance (optional)</Label>
              <Textarea rows={2} value={form.extra_prompt}
                        placeholder="e.g. focus on real-life applications, include diagram-based questions"
                        onChange={e => setForm(f => ({ ...f, extra_prompt: e.target.value }))}
                        data-testid="ai-extra-prompt" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                <b>{selectedIdx.size}</b> of {proposals.length} selected ·
                <span className="text-muted-foreground"> Click to edit, uncheck to skip</span>
              </p>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <RotateCcw className="h-4 w-4 mr-1" /> Regenerate
              </Button>
            </div>
            <div className="space-y-2">
              {proposals.map((q, i) => (
                <div key={q._key || `q-idx-${i}`}
                     className={`border rounded p-3 ${selectedIdx.has(i) ? 'bg-primary/5 border-primary/30' : 'bg-muted/20 opacity-70'}`}
                     data-testid={`ai-proposal-${i}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox checked={selectedIdx.has(i)} onCheckedChange={() => toggleSelect(i)}
                              data-testid={`ai-select-${i}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm">
                          <Badge variant="outline" className="mr-2">{q.question_type}</Badge>
                          {q.question_text}
                        </p>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(i)}
                                data-testid={`ai-edit-${i}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {q.options?.length > 0 && (
                        <ul className="list-none pl-4 text-xs text-muted-foreground mt-1 grid grid-cols-2 gap-x-3">
                          {q.options.map((o, j) => (
                            <li key={j}>({String.fromCharCode(97 + j)}) {o}</li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs">
                        <Badge variant="secondary">{q.marks} marks</Badge>
                        <Badge>{q.difficulty}</Badge>
                        {q.chapter && <Badge variant="outline">{q.chapter}</Badge>}
                        {q.learning_outcome && (
                          <span className="text-muted-foreground italic">{q.learning_outcome}</span>
                        )}
                      </div>
                      {q.correct_answer && (
                        <p className="text-xs text-emerald-700 mt-1">
                          <b>Answer:</b> {q.correct_answer}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={generate} disabled={generating} data-testid="ai-generate-btn">
                {generating
                  ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  : <Sparkles className="h-4 w-4 mr-2" />}
                {generating ? 'Generating…' : 'Generate Questions'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Discard</Button>
              <Button onClick={saveSelected} disabled={saving || selectedIdx.size === 0}
                      data-testid="ai-save-selected-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save {selectedIdx.size} to Bank
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Inline edit dialog */}
      {editDraft && (
        <Dialog open={!!editDraft} onOpenChange={(o) => !o && setEditIdx(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Question</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Question Text</Label>
                <Textarea rows={3} value={editDraft.question_text}
                          onChange={e => setEditDraft(d => ({ ...d, question_text: e.target.value }))} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={editDraft.question_type}
                          onValueChange={(v) => setEditDraft(d => ({ ...d, question_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {QUESTION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Marks</Label>
                  <Input type="number" step="0.5" value={editDraft.marks}
                         onChange={e => setEditDraft(d => ({ ...d, marks: parseFloat(e.target.value) }))} />
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <Select value={editDraft.difficulty}
                          onValueChange={(v) => setEditDraft(d => ({ ...d, difficulty: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editDraft.question_type === 'MCQ' && (
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i}>
                      <Label className="text-xs">Option {String.fromCharCode(97 + i)}</Label>
                      <Input value={(editDraft.options || [])[i] || ''}
                             onChange={e => {
                               const opts = [...(editDraft.options || ['', '', '', ''])];
                               opts[i] = e.target.value;
                               setEditDraft(d => ({ ...d, options: opts }));
                             }} />
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label>Correct Answer (private)</Label>
                <Input value={editDraft.correct_answer || ''}
                       onChange={e => setEditDraft(d => ({ ...d, correct_answer: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Chapter</Label>
                  <Input value={editDraft.chapter || ''}
                         onChange={e => setEditDraft(d => ({ ...d, chapter: e.target.value }))} />
                </div>
                <div>
                  <Label>Topic</Label>
                  <Input value={editDraft.topic || ''}
                         onChange={e => setEditDraft(d => ({ ...d, topic: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditIdx(null); setEditDraft(null); }}>Cancel</Button>
              <Button onClick={saveEdit}>
                <ChevronRight className="h-4 w-4 mr-1" /> Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default AIGenerateQuestionsDialog;
